import React, { useState } from 'react'
import AppDropdown from '../../../../components/AppDropdown'
import { tokenStatus, tokenBadgeClasses } from '../../services/telephonyHelpers'

/**
 * AccountCredentialsForm
 * ----------------------
 * Tenant-level Exotel credentials shared by every app (Tier 1 + Tier 2):
 *   - Account: Account SID, API Key (username), API Token (password), Domain
 *   - Customer: Customer ID, Customer Secret  → mints the customer token used to create apps
 *
 * "Validate customer token" mints + caches the customer JWT so you can confirm the
 * Customer ID / Secret pair before trying to create an app.
 */
export default function AccountCredentialsForm({
  account,
  setAccount,
  loading,
  saving,
  saveMsg,
  setSaveMsg,
  onSave,
}) {
  const [show, setShow] = useState({})
  const toggle = (key) => setShow((prev) => ({ ...prev, [key]: !prev[key] }))

  const customerToken = tokenStatus(account.customerTokenExpiresAt)

  const Secret = ({ label, field, placeholder, required = false }) => (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-on-surface uppercase tracking-wider block">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type={show[field] ? 'text' : 'password'}
          value={account[field] || ''}
          onChange={(e) => setAccount({ ...account, [field]: e.target.value })}
          required={required}
          placeholder={placeholder}
          className="w-full p-2.5 pr-10 rounded-xl border border-outline-variant/40 bg-surface-container-low text-xs font-mono text-on-surface focus:ring-2 focus:ring-primary focus:outline-none"
        />
        <button
          type="button"
          onClick={() => toggle(field)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface"
        >
          <span className="material-symbols-outlined text-base">
            {show[field] ? 'visibility_off' : 'visibility'}
          </span>
        </button>
      </div>
    </div>
  )

  return (
    <div className="bg-surface-container-lowest p-6 rounded-2xl border border-outline-variant/25 shadow-xs space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-outline-variant/15 pb-4">
        <div>
          <h2 className="text-sm font-extrabold uppercase tracking-wider font-display text-on-surface">Account Credentials</h2>
          <p className="text-xs text-on-surface-variant font-medium mt-0.5">
            Shared across every app. Used to mint the customer token that creates apps.
          </p>
        </div>
        <span
          className={`px-2.5 py-1 rounded-full border text-[10px] font-bold inline-flex items-center gap-1 ${tokenBadgeClasses(
            customerToken.state
          )}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-current inline-block opacity-70" />
          Customer token: {customerToken.label}
        </span>
      </div>

      {loading && (
        <div className="py-4 text-center text-xs text-on-surface-variant">Loading credentials…</div>
      )}

      {saveMsg && (
        <div
          className={`p-3.5 rounded-xl border text-xs font-semibold flex items-center justify-between ${
            saveMsg.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-base">
              {saveMsg.type === 'success' ? 'check_circle' : 'error'}
            </span>
            <span>{saveMsg.text}</span>
          </div>
          <button type="button" onClick={() => setSaveMsg(null)} className="font-bold ml-2">✕</button>
        </div>
      )}

      <form onSubmit={onSave} className="space-y-5">
        {/* Account tier */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-on-surface uppercase tracking-wider block">
              Account SID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={account.accountSid || ''}
              onChange={(e) => setAccount({ ...account, accountSid: e.target.value })}
              required
              placeholder="e.g. tezerp1m"
              className="w-full p-2.5 rounded-xl border border-outline-variant/40 bg-surface-container-low text-xs font-mono text-on-surface focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>
          <Secret label="API key (username)" field="exotelApiKey" placeholder="Exotel v2 API key" required />
          <Secret label="API token (password)" field="exotelApiToken" placeholder="Exotel v2 API token" required />
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-on-surface uppercase tracking-wider block">
              Domain <span className="text-red-500">*</span>
            </label>
            <AppDropdown
              value={account.exotelDomain || 'Mumbai'}
              onChange={(val) => setAccount({ ...account, exotelDomain: val })}
              options={[
                { value: 'Mumbai', label: 'Mumbai' },
                { value: 'Singapore', label: 'Singapore' },
              ]}
              placeholder="Select Domain"
            />
          </div>
        </div>

        {/* Shared ExoPhone */}
        <div className="border-t border-outline-variant/20 pt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-on-surface uppercase tracking-wider block">
                ExoPhone number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={account.exophone || ''}
                onChange={(e) => setAccount({ ...account, exophone: e.target.value })}
                required
                placeholder="e.g. 02263082367"
                className="w-full p-2.5 rounded-xl border border-outline-variant/40 bg-surface-container-low text-xs font-mono text-on-surface focus:ring-2 focus:ring-primary focus:outline-none"
              />
              <p className="text-[10px] text-on-surface-variant">
                Shared virtual number. Every agent mapping inherits it as VirtualNumber.
              </p>
            </div>
          </div>
        </div>

        {/* Customer tier */}
        <div className="border-t border-outline-variant/20 pt-5">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-bold text-on-surface">Customer (Integrations Core)</span>
            <span className="text-xs text-on-surface-variant">— mints the customer token used to create apps</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Secret label="Customer ID" field="customerId" placeholder="f371f880-f9ab-4f43-b640…" required />
            <Secret label="Customer Secret" field="customerSecret" placeholder="7db1bd32-1590-4323…" required />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-on-primary font-bold text-xs shadow-md transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-base">
              {saving ? 'progress_activity' : 'save'}
            </span>
            <span>{saving ? 'Validating & saving…' : 'Save credentials'}</span>
          </button>
        </div>
      </form>
    </div>
  )
}
